/**
 * DashboardPage - Redesigned main landing page for the GBrands field operations app.
 *
 * Displays a personalized welcome header, hero stat card for active work orders,
 * secondary stat cards (pending inspections, completed this week), the shared
 * DashboardTaskList section (today + yesterday work orders and inspections in
 * the user's scope), and a desktop-only "Recent Field Activity" table.
 *
 * Data sources:
 *	 - Active work orders count:  GET /work-orders?status=in_progress
 *	 - Pending inspections count: GET /inspections?status=pending
 *	 - Completed this week:		  GET /dashboard/metrics?from={monday}&to={today}
 *	 - My tasks:				  GET /scheduled-events?from={yesterday}&to={today}
 *								  (via the DashboardTaskList organism)
 *	 - Recent activity:			  Combined work orders + inspections sorted by updatedAt
 *
 * Accessible by: all authenticated users (protected by <ProtectedRoute>).
 */
import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { TieredDashboardView } from './TieredDashboardView';
import { DashboardTaskList } from '../../components/organisms';
import { workOrderService, normalizeWorkOrder } from '../../services/workOrderService';
import type { WorkOrder, PaginatedResponse } from '../../services/workOrderService';
import { inspectionService, normalizeInspection } from '../../services/inspectionService';
import type { Inspection, PaginatedInspectionResponse } from '../../services/inspectionService';
import { camelizeKeys } from '../../services/api';
import { dashboardService } from '../../services/dashboardService';
import type { DashboardMetrics } from '../../services/dashboardService';
import { formatDateTime, formatRelative, isoUtcDaysAgo } from '../../utils/formatDate';
import { formatStatusLabel } from '../../utils/formatLabel';
import { qk } from '../../lib/queryKeys';
import { PageContainer } from '../../components/templates/PageContainer';

/* ============================================
 * STATUS BADGE COLOR MAPS (for the activity table)
 * ============================================ */
const STATUS_BADGE_COLORS: Record<string, string> = {
	draft: 'bg-gray-100 text-gray-700',
	submitted: 'bg-yellow-100 text-yellow-700',
	approved: 'bg-blue-100 text-blue-700',
	in_progress: 'bg-indigo-100 text-indigo-700',
	on_hold: 'bg-orange-100 text-orange-700',
	completed: 'bg-green-100 text-green-700',
	invoiced: 'bg-purple-100 text-purple-700',
	closed: 'bg-gray-100 text-gray-600',
	cancelled: 'bg-red-100 text-red-700',
	/* Inspection statuses */
	pending: 'bg-yellow-100 text-yellow-700',
	observations_complete: 'bg-indigo-100 text-indigo-700',
	corrections_in_progress: 'bg-orange-100 text-orange-700',
};

/* ============================================
 * SKELETON COMPONENTS
 * Animated placeholder boxes shown while data is loading.
 * Uses Tailwind's animate-pulse for a subtle breathing effect.
 * ============================================ */

/** Skeleton for the hero stat card (large card at the top) */
const HeroCardSkeleton: React.FC = () => (
	<div className="rounded-12px p-24px bg-gray-200 animate-pulse h-144px" />
);

/** Skeleton for smaller stat cards in the secondary row */
const StatCardSkeleton: React.FC = () => (
	<div className="rounded-12px p-20px bg-gray-100 animate-pulse h-100px" />
);

/** Skeleton for the activity table rows */
const TableRowSkeleton: React.FC = () => (
	<tr className="border-b border-brand-border">
		<td className="py-12px px-16px"><div className="h-14px w-120px bg-gray-100 animate-pulse rounded-4px" /></td>
		<td className="py-12px px-16px"><div className="h-14px w-100px bg-gray-100 animate-pulse rounded-4px" /></td>
		<td className="py-12px px-16px"><div className="h-14px w-128px bg-gray-100 animate-pulse rounded-4px" /></td>
		<td className="py-12px px-16px"><div className="h-14px w-80px bg-gray-100 animate-pulse rounded-4px" /></td>
		<td className="py-12px px-16px"><div className="h-20px w-72px bg-gray-100 animate-pulse rounded-4px" /></td>
	</tr>
);

/* ============================================
 * UNIFIED ACTIVITY ITEM TYPE
 * Combines work orders and inspections into a single shape for the
 * Recent Field Activity table so they can be sorted and displayed together.
 * ============================================ */
interface ActivityItem {
	id: string;
	type: 'work_order' | 'inspection';
	timestamp: string;
	personName: string;
	location: string;
	operation: string;
	status: string;
}

/* formatStatusLabel is shared via utils/formatLabel — see import below. */

/* ============================================
 * MAIN COMPONENT — TIER DISPATCHER
 * ============================================
 * Picks the right dashboard view based on the authenticated user's
 * roles. Super Admin / Developer get the rich SuperAdminDashboard;
 * Admin / Manager / Company Admin get ManagerDashboard; everyone
 * else (Technician / Reviewer) falls through to the legacy
 * tech-tier surface (TechDashboardView below) which has been the
 * default dashboard until this refactor.
 *
 * Kept as a thin dispatcher so each tier's component can evolve
 * independently without entangling its hooks with the others.
 */
const DashboardPage: React.FC = () => {
	const { hasAnyRole } = useAuth();
	const isSuperTier = hasAnyRole(['super_admin', 'developer']);
	const isManagerTier = hasAnyRole(['admin', 'manager', 'company_admin']);
	if (isSuperTier || isManagerTier) {
		return <TieredDashboardView tier={isSuperTier ? 'super_admin' : 'manager'} />;
	}
	return <TechDashboardView />;
};

/* ============================================
 * TECH-TIER DASHBOARD VIEW (legacy default)
 * ============================================
 * Preserved as-is from the previous DashboardPage implementation
 * for technicians and reviewers. Owns its own state + fetches.
 * The dispatcher above renders this when the user has neither
 * super-admin nor manager-tier roles.
 */
const TechDashboardView: React.FC = () => {
	const navigate = useNavigate();
	/* Pull current user from auth context for personalization */
	const { user } = useAuth();

	/**
	 * Completed this week — from /dashboard/metrics with a Monday..today
	 * window. counts.completedCurrent combines work orders + inspections,
	 * already scoped server-side to the tech's own records. Returns null
	 * on failure so the card shows "--" like the other stat cards.
	 *
	 * The window is computed in UTC because completion_date is stamped from
	 * the server's UTC "today" — a local-calendar window would miss records
	 * completed during the evening hours in western timezones.
	 */
	const completedThisWeekQuery = useQuery({
		queryKey: qk.dashboard.metrics('tech-week'),
		queryFn: async () => {
			try {
				/* Days since Monday: getUTCDay() is 0=Sun..6=Sat */
				const daysSinceMonday = (new Date().getUTCDay() + 6) % 7;
				const res = await dashboardService.metrics({
					from: isoUtcDaysAgo(daysSinceMonday),
					to: isoUtcDaysAgo(0),
				});
				const metrics = (res.data as unknown as { data: DashboardMetrics | null })?.data;
				return metrics?.counts?.completedCurrent ?? null;
			} catch {
				return null;
			}
		},
	});
	const completedThisWeek = completedThisWeekQuery.data ?? null;

	/**
	 * Active (in_progress) work order count for the hero stat card.
	 * Prefers the paginated response's total; otherwise counts the array.
	 * Returns null on failure so the card shows "--".
	 */
	const activeWoQuery = useQuery({
		queryKey: qk.workOrders.list({ status: 'in_progress', _dashboard: 'count' }),
		queryFn: async () => {
			try {
				const response = await workOrderService.getAll({ status: 'in_progress' });
				const data = response.data as PaginatedResponse<WorkOrder>;
				if (data && typeof data.total === 'number') return data.total;
				if (data && Array.isArray(data.data)) return data.data.length;
				if (Array.isArray(response.data)) return (response.data as unknown as WorkOrder[]).length;
				return null;
			} catch {
				/* Silently fall back — the card will show "--" on null */
				return null;
			}
		},
	});
	const activeWoCount = activeWoQuery.data ?? null;

	/**
	 * Pending inspection count for the secondary stat card. Same shape
	 * handling as the work-order count above.
	 */
	const pendingInspQuery = useQuery({
		queryKey: qk.inspections.list({ status: 'pending', _dashboard: 'count' }),
		queryFn: async () => {
			try {
				const response = await inspectionService.getAll({ status: 'pending' });
				const data = response.data as PaginatedInspectionResponse;
				if (data && typeof data.total === 'number') return data.total;
				if (data && Array.isArray(data.data)) return data.data.length;
				if (Array.isArray(response.data)) return (response.data as unknown as Inspection[]).length;
				return null;
			} catch {
				return null;
			}
		},
	});
	const pendingInspCount = pendingInspQuery.data ?? null;

	/* The stat cards share a loading skeleton — show it until all three
	 * count queries have settled (matching the old Promise.allSettled gate). */
	const statsLoading = activeWoQuery.isLoading || pendingInspQuery.isLoading || completedThisWeekQuery.isLoading;

	/**
	 * Recent activity — combines work orders and inspections, sorted by
	 * updatedAt descending, keeping the latest 10. Each endpoint is fetched
	 * with Promise.allSettled so one failure doesn't drop the other's rows.
	 */
	const activityQuery = useQuery({
		queryKey: ['dashboardActivity', 'recent'] as const,
		queryFn: async () => {
			/* Fire both requests in parallel for faster loading */
			const [woResponse, inspResponse] = await Promise.allSettled([
				workOrderService.getAll({}),
				inspectionService.getAll({}),
			]);

			const items: ActivityItem[] = [];

			/* Map work orders into unified ActivityItem shape. The raw API rows
			   are snake_case with nested relations — run them through the same
			   camelize + normalize pipeline the list pages use, otherwise the
			   flattened fields read here (assigneeName, locationName,
			   projectName) are all undefined and every row renders as
			   "Unassigned" with no location or operation. */
			if (woResponse.status === 'fulfilled') {
				const woData = woResponse.value.data as PaginatedResponse<WorkOrder>;
				const rawWos = woData?.data ?? (Array.isArray(woResponse.value.data) ? woResponse.value.data as unknown[] : []);
				const woList = (camelizeKeys(rawWos) as Record<string, unknown>[]).map(normalizeWorkOrder);
				woList.forEach((wo) => {
					items.push({
						id: wo.id,
						type: 'work_order',
						timestamp: wo.updatedAt,
						personName: wo.assigneeName || 'Unassigned',
						location: wo.locationName || wo.projectAddress || '--',
						operation: wo.projectName,
						status: wo.status,
					});
				});
			}

			/* Map inspections into unified ActivityItem shape — same
			   camelize + normalize treatment as the work orders above. */
			if (inspResponse.status === 'fulfilled') {
				const inspData = inspResponse.value.data as PaginatedInspectionResponse;
				const rawInsps = inspData?.data ?? (Array.isArray(inspResponse.value.data) ? inspResponse.value.data as unknown[] : []);
				const inspList = (camelizeKeys(rawInsps) as Record<string, unknown>[]).map(normalizeInspection);
				inspList.forEach((insp) => {
					items.push({
						id: insp.id,
						type: 'inspection',
						timestamp: insp.updatedAt,
						personName: insp.inspectorName || 'Unassigned',
						location: insp.buildingName,
						operation: `Inspection — ${insp.companyName}`,
						status: insp.status,
					});
				});
			}

			/* Sort by most recent update and keep top 10 */
			items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
			return items.slice(0, 10);
		},
	});
	const activityItems: ActivityItem[] = useMemo(() => activityQuery.data ?? [], [activityQuery.data]);
	const activityLoading = activityQuery.isLoading;

	/* Extract the user's first name for the welcome header */
	const firstName = user?.firstName ?? 'User';

	/* Monday of the current UTC week — the same window the Completed This
	 * Week metric queries, reused by that card's deep link into the WO list. */
	const weekStartUtc = isoUtcDaysAgo((new Date().getUTCDay() + 6) % 7);

	/* Shared focus treatment for the clickable stat cards below. */
	const cardFocusClass = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2';

	return (
		<PageContainer block="dashboard-page" dataRef="page-dashboard-page">
			{/* ================================================
			 * PAGE HEADER
			 * Personalized welcome message with Lulo heading font.
			 * ================================================ */}
			<div className="mb-24px">
				<h1 className="text-24px font-heading uppercase tracking-wide text-brand-heading">
					Welcome, {firstName}
				</h1>
				<p className="mt-4px text-14px text-brand-text-muted font-sans">
					Ready for today's field operations?
				</p>
			</div>

			{/* ================================================
			 * HERO STAT CARD — ACTIVE WORK ORDERS
			 * Large card with brand-accent (dark blue) background,
			 * white text, clipboard icon decoration on the right.
			 * Deep-links to the WO list pre-filtered to In Progress —
			 * the same query the count comes from.
			 * ================================================ */}
			{statsLoading ? (
				<HeroCardSkeleton />
			) : (
				<Link
					to="/work-orders?status=in_progress"
					aria-label="View all active work orders"
					className={`block rounded-12px p-24px mb-16px relative overflow-hidden bg-brand-accent hover:bg-brand-accent-dark transition-colors ${cardFocusClass}`}
				>
					{/* Decorative clipboard icon positioned in the top-right corner */}
					<div className="absolute right-16px top-16px opacity-20">
						<svg className="w-64px h-64px text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
							<path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z" />
						</svg>
					</div>
					{/* Card label */}
					<p className="text-12px font-heading uppercase tracking-wider text-white/70 mb-8px">
						ACTIVE WORK ORDERS
					</p>
					{/* Large metric number */}
					<p className="text-48px font-bold text-white">
						{activeWoCount !== null ? activeWoCount : '--'}
					</p>
					{/* Contextual subtitle */}
					<p className="text-14px text-white/60 mt-8px">
						Currently in progress across all locations
					</p>
				</Link>
			)}

			{/* ================================================
			 * SECONDARY STAT CARDS ROW
			 * Two smaller cards displayed side by side: Pending Inspections
			 * and Completed This Week.
			 * ================================================ */}
			<div className="grid grid-cols-2 gap-12px mb-32px">
				{statsLoading ? (
					<>
						<StatCardSkeleton />
						<StatCardSkeleton />
					</>
				) : (
					<>
						{/* Pending Inspections stat card — deep-links to the
						 * inspection list pre-filtered to Pending. */}
						<Link
							to="/inspections?status=pending"
							aria-label="View all pending inspections"
							className={`block bg-white rounded-12px border border-brand-border p-20px shadow-sm hover:border-brand-primary transition-colors ${cardFocusClass}`}
						>
							<p className="text-12px font-heading uppercase tracking-wider text-brand-text-muted mb-6px">
								PENDING INSPECTIONS
							</p>
							<p className="text-30px font-bold text-brand-accent">
								{pendingInspCount !== null ? pendingInspCount : '--'}
							</p>
							<p className="text-12px text-brand-text-muted mt-4px">
								Awaiting review
							</p>
						</Link>

						{/* Completed This Week stat card — WOs + inspections the
						 * user completed since Monday, from /dashboard/metrics.
						 * Deep-links to the WO list filtered to Completed since
						 * Monday (the list's date range matches on created_at,
						 * so the row set can differ slightly from this count,
						 * which matches on completion_date). */}
						<Link
							to={`/work-orders?status=completed&date_from=${weekStartUtc}`}
							aria-label="View work orders completed this week"
							className={`block bg-white rounded-12px border border-brand-border p-20px shadow-sm hover:border-brand-primary transition-colors ${cardFocusClass}`}
						>
							<p className="text-12px font-heading uppercase tracking-wider text-brand-text-muted mb-6px">
								COMPLETED THIS WEEK
							</p>
							<p className="text-30px font-bold text-green-600">
								{completedThisWeek !== null ? completedThisWeek : '--'}
							</p>
							<p className="text-12px text-brand-text-muted mt-4px">
								Tasks finished
							</p>
						</Link>
					</>
				)}
			</div>

			{/* ================================================
			 * MY TASKS SECTION
			 * Shared DashboardTaskList organism — today + yesterday work
			 * orders and inspections in the user's scope, with the
			 * simplified completion/user/company filter and "view all"
			 * links to the full list pages.
			 * ================================================ */}
			<DashboardTaskList className="mb-32px" />

			{/* ================================================
			 * RECENT FIELD ACTIVITY TABLE (Desktop Only)
			 * Combines work orders and inspections sorted by most recent
			 * update. Shows timestamp, person, location, operation, and status.
			 * Hidden on mobile for a cleaner experience.
			 * ================================================ */}
			<div className="hidden tablet:block">
				{/* Section header with export CSV link */}
				<div className="flex items-center justify-between mb-16px">
					<h2 className="text-18px font-heading uppercase tracking-wide text-brand-heading">
						RECENT FIELD ACTIVITY
					</h2>
					{/* Export CSV link — placeholder functionality (logs to console) */}
					<button
						type="button"
						onClick={() => {
							/* Future: implement CSV export of recent activity */
							console.info('[DashboardPage] Export CSV clicked — not yet implemented');
						}}
						className="text-12px font-semibold uppercase tracking-wide text-brand-primary hover:text-brand-primary-hover transition-colors"
					>
						EXPORT CSV
					</button>
				</div>

				{/* Activity table */}
				<div className="bg-white rounded-12px border border-brand-border shadow-sm overflow-hidden">
					<div className="overflow-x-auto">
						<table className="w-full text-14px font-sans">
							<thead>
								<tr className="bg-brand-surface border-b border-brand-border">
									<th className="text-left py-12px px-16px font-semibold text-brand-text-muted text-12px uppercase tracking-wide">
										Timestamp
									</th>
									<th className="text-left py-12px px-16px font-semibold text-brand-text-muted text-12px uppercase tracking-wide">
										Inspector / Assignee
									</th>
									<th className="text-left py-12px px-16px font-semibold text-brand-text-muted text-12px uppercase tracking-wide">
										Location
									</th>
									<th className="text-left py-12px px-16px font-semibold text-brand-text-muted text-12px uppercase tracking-wide">
										Operation
									</th>
									<th className="text-left py-12px px-16px font-semibold text-brand-text-muted text-12px uppercase tracking-wide">
										Status
									</th>
								</tr>
							</thead>
							<tbody>
								{activityLoading ? (
									/* Loading skeleton rows */
									<>
										<TableRowSkeleton />
										<TableRowSkeleton />
										<TableRowSkeleton />
										<TableRowSkeleton />
										<TableRowSkeleton />
									</>
								) : activityItems.length === 0 ? (
									/* Empty state */
									<tr>
										<td colSpan={5} className="py-32px text-center text-14px text-brand-text-muted">
											No recent activity to display.
										</td>
									</tr>
								) : (
									/* Rendered activity rows */
									activityItems.map((item) => (
										<tr
											key={`${item.type}-${item.id}`}
											className="border-b border-brand-border hover:bg-brand-surface cursor-pointer transition-colors"
											onClick={() => {
												/* Navigate to the detail page for this item */
												if (item.type === 'work_order') {
													navigate(`/work-orders/${item.id}`);
												} else {
													navigate(`/inspections/${item.id}`);
												}
											}}
										>
											{/* Timestamp column — shows relative time with full datetime on hover */}
											<td className="py-12px px-16px text-12px text-brand-text-muted whitespace-nowrap" title={formatDateTime(item.timestamp)}>
												{formatRelative(item.timestamp)}
											</td>
											{/* Person column — avatar circle with initials + name */}
											<td className="py-12px px-16px">
												<div className="flex items-center gap-8px">
													{/* Avatar circle showing first letter of person's name */}
													<div className="w-28px h-28px rounded-full bg-brand-accent flex items-center justify-center flex-shrink-0">
														<span className="text-12px font-bold text-white">
															{/* Safely grab first initial, fallback to '?' if name is missing */}
															{(item.personName ?? '?').charAt(0).toUpperCase()}
														</span>
													</div>
													<span className="text-12px text-brand-text line-clamp-1">
														{/* Display person name or fallback for unassigned items */}
														{item.personName ?? 'Unassigned'}
													</span>
												</div>
											</td>
											{/* Location column */}
											<td className="py-12px px-16px text-12px text-brand-text-muted line-clamp-1">
												{item.location}
											</td>
											{/* Operation column — shows type icon + operation name */}
											<td className="py-12px px-16px text-12px text-brand-text line-clamp-1">
												{item.operation}
											</td>
											{/* Status badge column */}
											<td className="py-12px px-16px">
												<span className={[
													'inline-flex items-center px-8px py-2px rounded-full text-12px font-medium whitespace-nowrap',
													STATUS_BADGE_COLORS[item.status] || 'bg-gray-100 text-gray-700',
												].join(' ')}>
													{formatStatusLabel(item.status)}
												</span>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</PageContainer>
	);
};

export default DashboardPage;
